import React from "react";
import { Link } from "react-router-dom";
import { SiX, SiFacebook, SiLinkedin, SiInstagram } from "react-icons/si";
import { FiHelpCircle, FiInfo } from "react-icons/fi";

const Footer = ({ className }) => {
  return (
    <footer
      className={`w-full bg-gradient-to-br from-[#47001C] via-[#70062b] to-[#971132] border-t border-rose-950/40 text-white font-poppins relative overflow-hidden ${className}`}
    >
      {/* Background Decorative Glows */}
      <div className="absolute top-[-100px] left-[-100px] w-[300px] h-[300px] bg-rose-500/10 rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute bottom-[-150px] right-[-50px] w-[400px] h-[400px] bg-rose-600/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 relative z-10">
        
        {/* Main Footer Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-8 mb-12">
          
          {/* Column 1: Brand Info */}
          <div className="flex flex-col space-y-4">
            <Link to="/" className="inline-block transition-transform duration-300 hover:scale-105">
              <img
                src="/images/Love_All.avif"
                alt="Love All Logo"
                className="w-32 object-contain"
              />
            </Link>
            <p className="text-sm text-gray-300 leading-relaxed max-w-sm">
              A Social Loyalty Program by Atria Foundation.
            </p>
            <div className="flex flex-col space-y-3 pt-4 border-t border-white/10 text-sm">
              <Link
                to="/faq"
                className="group flex items-center gap-2.5 text-gray-300 hover:text-[#FBD784] transition-all duration-300 w-fit"
              >
                <span className="p-1.5 rounded-lg bg-white/5 group-hover:bg-[#FBD784]/15 transition-colors">
                  <FiHelpCircle className="w-3.5 h-3.5 text-gray-400 group-hover:text-[#FBD784] transition-colors" />
                </span>
                <span className="font-medium tracking-wide">Frequently Asked Questions</span>
              </Link>
              <a
                href="https://www.atriafoundation.org/"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-2.5 text-gray-300 hover:text-[#FBD784] transition-all duration-300 w-fit"
              >
                <span className="p-1.5 rounded-lg bg-white/5 group-hover:bg-[#FBD784]/15 transition-colors">
                  <FiInfo className="w-3.5 h-3.5 text-gray-400 group-hover:text-[#FBD784] transition-colors" />
                </span>
                <span className="font-medium tracking-wide">About Atria Foundation</span>
              </a>
            </div>
          </div>

          {/* Column 2: Navigation Links */}
          <div className="flex flex-col space-y-4">
            <h4 className="text-[#FBD784] text-xs font-bold tracking-widest uppercase">
              Explore
            </h4>
            <nav className="flex flex-col space-y-2.5">
              {[
                { to: "/", label: "Home" },
                { to: "/donate-and-join", label: "Donate & Join" },
                { to: "/our-work", label: "Our Work" },
                { to: "/about", label: "About Us" },
                { to: "/contact", label: "Contact Us" },
                { href: "https://www.atriafoundation.org/donation", label: "Contribute", external: true }
              ].map((link, idx) => (
                link.external ? (
                  <a
                    key={idx}
                    href={link.href}
                    className="text-sm text-gray-300 hover:text-white transition-all duration-300 hover:translate-x-1.5 inline-block w-fit"
                  >
                    {link.label}
                  </a>
                ) : (
                  <Link
                    key={idx}
                    to={link.to}
                    className="text-sm text-gray-300 hover:text-white transition-all duration-300 hover:translate-x-1.5 inline-block w-fit"
                  >
                    {link.label}
                  </Link>
                )
              ))}
            </nav>
          </div>

          {/* Column 3: Contact Details */}
          <div className="flex flex-col space-y-4">
            <h4 className="text-[#FBD784] text-xs font-bold tracking-widest uppercase">
              Contact Us
            </h4>
            <div className="flex flex-col space-y-3 text-sm text-gray-300">
              <p className="hover:text-white transition-colors duration-300 w-fit">
                +91 89711 01055
              </p>
              <a
                href="mailto:contact@atriafoundation.org"
                className="hover:text-white hover:underline transition-colors duration-300 block w-fit"
              >
                contact@atriafoundation.org
              </a>
            </div>
          </div>

          {/* Column 4: Location & Socials */}
          <div className="flex flex-col space-y-4">
            <h4 className="text-[#FBD784] text-xs font-bold tracking-widest uppercase">
              Location & Connect
            </h4>
            <a
              href="https://maps.app.goo.gl/serMnQmJcWphFNUz5"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-gray-300 hover:text-white transition-colors duration-300 leading-relaxed block hover:underline"
            >
              Atria Foundation, ASKB Campus
              <br />
              Adjacent To Bangalore Baptist Hospital,
              <br />
              Hebbal, Bengaluru - 560 024
            </a>
            
            {/* Social Icons */}
            <div className="flex items-center gap-3 pt-2">
              {[
                { href: "https://x.com/AtriaFoundation", icon: SiX },
                { href: "https://www.facebook.com/atriafoundation", icon: SiFacebook },
                { href: "https://www.linkedin.com/company/atria-foundation/", icon: SiLinkedin },
                { href: "https://www.instagram.com/atriafoundation", icon: SiInstagram }
              ].map((soc, idx) => (
                <a
                  key={idx}
                  href={soc.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 bg-white/5 border border-white/10 rounded-full flex items-center justify-center text-white/80 hover:text-white hover:bg-rose-600 hover:border-rose-500 hover:scale-110 transition-all duration-300 shadow-md"
                >
                  <soc.icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

        </div>

        {/* Divider line */}
        <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-white/10 to-transparent my-8" />

        {/* Bottom Section: Payments & Copyright */}
        <div className="flex flex-col items-center justify-center space-y-6 text-center">
          <div className="space-y-3 w-full">
            <div className="text-xs font-semibold tracking-wider text-gray-400 uppercase">
              100% secure donations by
            </div>
            <img
              src="https://razorpay.com/logo.svg"
              alt="Razorpay"
              className="h-8 mx-auto object-contain brightness-0 invert opacity-80 hover:opacity-100 transition-opacity duration-300"
            />
            <div className="flex flex-wrap justify-center items-center gap-3 pt-2 max-w-lg mx-auto">
              {[
                "Visa.avif",
                "RuPay.avif",
                "Maestro.avif",
                "mastercard.avif",
                "AmericanExpress.avif",
                "UPI.avif",
                "Phonepay.avif",
                "Gpay.avif",
                "Paytm.avif",
              ].map((img, index) => (
                <div
                  key={index}
                  className="bg-white border border-white/10 p-1.5 px-2.5 rounded-lg shadow-sm hover:scale-105 hover:bg-white hover:border-white/20 transition-all duration-300 backdrop-blur-sm"
                >
                  <img
                    src={`/images/${img}`}
                    alt={img.split(".")[0]}
                    className="h-5 w-auto object-contain filter brightness-95"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Footer Copyright Text */}
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4 text-xs text-gray-500 pt-2 tracking-wide">
            <span>© Copyrights LoveAll 2026 | All Rights Reserved</span>
            <span className="hidden sm:inline">|</span>
            <div className="flex gap-4">
              <Link to="/terms" className="hover:text-white transition-colors duration-300">
                Terms & Conditions
              </Link>
              <Link to="/privacy" className="hover:text-white transition-colors duration-300">
                Privacy Policy
              </Link>
            </div>
          </div>
        </div>

      </div>
    </footer>
  );
};

export default Footer;
